import { useRef, useEffect, useState } from 'react';
import CardContainer from '../components/CardContainer';
import ContainerLayout from '../components/ContainerLayout';
import Footer from '../components/Footer';
import Main from '../components/Main';
import TopHeader from '../components/TopHeader';
import metricsService from '../services/metrics';
import { IdPs } from '../constants/providers';
import { LAST_USED_PROVIDER_KEY } from '../constants/storage';
import { localStorageAdapter } from '../services/storage';
import { AppConfig } from '../types/app';
import { useIntl } from 'react-intl';
import LogoutInstructions from '../components/LogoutInstructions';
import { renderProviderName } from '../utils/renderProviderName';
import { ContentLayout, Spinner } from '@amzn/awsui-components-react';
import globalSignOut from '../services/globalSignOut';
import builderIdShimLogout from '../services/builderIdShimLogout';

export const Logout = ({ config }: { config: AppConfig }) => {
  const { formatMessage } = useIntl();

  const { idp } = JSON.parse(
    localStorageAdapter.getItem(LAST_USED_PROVIDER_KEY) || '{}'
  );
  let providerDisplayName = idp
    ? renderProviderName(idp.idp, formatMessage)
    : undefined;

  const metricsPublisher = useRef(metricsService.getPublisher('Logout'));

  const urlParams = new URLSearchParams(window.location.search);
  const bidLogoutComplete = urlParams.get('bid_logout_complete') === 'true';
  const clientIdFromUrl = urlParams.get('client_id');
  const [logoutInProgress, setLogoutInProgress] = useState(
    !bidLogoutComplete &&
      (config.enableGlobalSignOut === 'true' ||
        config.enableBuilderIdShimLogout === 'true')
  );

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor('Impressions', 1);
  }, []);

  useEffect(() => {
    if (bidLogoutComplete) return;

    const shouldCallShimLogout =
      config.enableBuilderIdShimLogout === 'true' &&
      config.builderIdShimLogoutUrl &&
      idp?.idp === IdPs.AWSBuilderID &&
      clientIdFromUrl;

    const performLogout = async () => {
      if (config.enableGlobalSignOut === 'true') {
        try {
          await globalSignOut(config.globalSignOutApiPath);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error calling globalSignOut:', error);
        }
      }

      if (shouldCallShimLogout) {
        try {
          await builderIdShimLogout(
            config.builderIdShimLogoutUrl!,
            clientIdFromUrl as string
          );
          return; // page will navigate away, keep spinner showing
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error calling builderIdShimLogout:', error);
        }
      }

      setLogoutInProgress(false);
    };

    performLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!providerDisplayName) {
      metricsPublisher.current.publishCounterMonitor(
        'ProviderDisplayNameNotfound',
        1
      );
    }
  }, [providerDisplayName]);

  const enableCookieComponent = Boolean(
    config?.enableCookieComponent === 'true'
  );

  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <ContainerLayout enableResponsiveStyles>
          <ContentLayout>
            {logoutInProgress ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Spinner size="large" data-testid="logout-spinner" />
              </div>
            ) : (
              <CardContainer>
                <LogoutInstructions config={config} idp={idp?.idp as IdPs} />
              </CardContainer>
            )}
          </ContentLayout>
        </ContainerLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
};
